import { useMemo } from 'react';

export const usePermissions = () => {
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  }, [localStorage.getItem('user')]);

  const isAdmin = user?.role === 'ADMIN';
  const isDirecteur = user?.role === 'DIRECTEUR';
  const isScolarite = user?.role === 'SCOLARITE';
  const isEnseignant = user?.role === 'ENSEIGNANT';
  const isEtudiant = user?.role === 'ETUDIANT';
  const isConsultant = user?.role === 'CONSULTANT';
  const isResponsableScolarite = user?.role === 'RESPONSABLE_SCOLARITE';
  const isChefFiliere = user?.role === 'CHEF_FILIERE';
  const isDirectionFinanciere = user?.role === 'DIRECTION_FINANCIERE';
  const isDirectionGenerale = user?.role === 'DIRECTION_GENERALE';
  const isCoordinateurPedagogique = user?.role === 'COORDINATEUR_PEDAGOGIQUE';
  const isAuthenticated = !!user?.role;

  // Direction : admin + directeur uniquement (etudes, inscriptions, paiements).
  const isDirection = isAdmin || isDirecteur;

  const canManageScolarite = isAdmin || isDirecteur || isScolarite || isResponsableScolarite;

  // Chef de filiere : gere les seances, les emplois du temps, le suivi des
  // absences ainsi que les notes et les stages.
  const peutGererEnseignement = isAdmin || isDirecteur || isChefFiliere || isScolarite || isResponsableScolarite;

  // Direction financiere : gere les paiements ; le suivi des seances et les
  // listes d'etudiants sont en consultation seule.
  const peutGererFinances = isAdmin || isDirecteur || isDirectionFinanciere;

  // Direction generale : consultation seule de tous les modules, sans droits
  // d'ecriture (sauf parametres, qui restent reserves a l'administrateur).
  const peutConsulter = isDirectionGenerale;

  // Coordinateur pedagogique : consultation seule des etudiants, des notes, des
  // emplois du temps, du suivi des seances et du suivi des absences. Il n'a aucun
  // droit d'ecriture, et pas acces aux annonces, messages ni parametres.
  const peutConsulterPedagogique = isCoordinateurPedagogique;

  const permissions = {
    // Actions CRUD
    canCreate: isDirection,
    canEdit: isDirection,
    canDelete: isDirection,
    canViewAll: isAdmin || isDirecteur || isConsultant || peutConsulter,
    
    // Gestion des etudiants / inscriptions / paiements
    canCreateEtudiant: canManageScolarite,
    canEditEtudiant: canManageScolarite,
    canDeleteEtudiant: isAdmin || isDirecteur || isScolarite || isResponsableScolarite,
    canCreateInscription: isDirection || isResponsableScolarite,
    canEditInscription: isDirection || isResponsableScolarite,
    canDeleteInscription: isDirection || isResponsableScolarite,
    canCreatePaiement: isDirection,
    canEditPaiement: isDirection,
    canDeletePaiement: isDirection,
    
    // Acces aux pages
    canAccessEtudiants: isAdmin || isDirecteur || isScolarite || isResponsableScolarite || isConsultant || isDirectionFinanciere || peutConsulter || peutConsulterPedagogique,
    canAccessEnseignants: isAdmin || isDirecteur || isScolarite || isResponsableScolarite || isConsultant || isDirectionFinanciere || peutConsulter,
    canAccessInscriptions: isAdmin || isDirecteur || isScolarite || isResponsableScolarite || isConsultant || isDirectionFinanciere || peutConsulter,
    canAccessPaiements: isAdmin || isDirecteur || isScolarite || isResponsableScolarite || isConsultant || isDirectionFinanciere || peutConsulter,
    canAccessNotes: isAdmin || isDirecteur || isChefFiliere || isEnseignant || isResponsableScolarite || peutConsulter || peutConsulterPedagogique,
    canAccessPresences: isAdmin || isDirecteur || isEnseignant || isResponsableScolarite || peutConsulter,
    // Consultation de l appel : la scolarite consulte et imprime les appels deja effectues.
    canConsulterAppel: isAdmin || isDirecteur || isEnseignant || isScolarite || isResponsableScolarite || isDirectionFinanciere,
    canAccessPassation: isAdmin || isDirecteur,
    canAccessEmploiTemps: isAdmin || isDirecteur || isEnseignant || isEtudiant || isChefFiliere || isResponsableScolarite || peutConsulter || peutConsulterPedagogique,
    // Emploi du temps (vue direction) : consultation + modification par groupe/prof.
    canAccessEmploiDirection: isAdmin || isDirecteur || isScolarite || isResponsableScolarite || isChefFiliere || peutConsulter || peutConsulterPedagogique,
    canAccessParametres: isAdmin,
    canAccessUsers: isAdmin,
    canAccessDashboard: isAdmin || isDirecteur || isScolarite || isResponsableScolarite || isEnseignant || isEtudiant || isConsultant || isChefFiliere || isDirectionFinanciere || peutConsulter || peutConsulterPedagogique,
    canAccessAnnonces: isAdmin || isDirecteur || isScolarite || isResponsableScolarite || isEtudiant || isConsultant || isChefFiliere || isDirectionFinanciere || peutConsulter,
    
    // Suivi des seances : consultation pour tous les roles autorises, gestion
    // (valider, rattrapage, annuler l appel) pour la direction et le chef de filiere.
    canAccessSuiviSeances: isAdmin || isDirecteur || isScolarite || isResponsableScolarite || isEnseignant || isChefFiliere || isDirectionFinanciere || peutConsulter || peutConsulterPedagogique,
    canGererSuiviSeances: peutGererEnseignement,
    // Suivi des absences des etudiants : gestion pour la direction et le chef de filiere.
    canAccessSuiviAbsences: isAdmin || isDirecteur || isScolarite || isResponsableScolarite || isChefFiliere || peutConsulter || peutConsulterPedagogique,

    // Modification des donnees : la direction financiere consulte les listes
    // d etudiants / inscriptions / enseignants en lecture seule.
    canModifierEtudiants: isAdmin || isDirecteur || isScolarite || isResponsableScolarite || isConsultant,
    canModifierInscriptions: isAdmin || isDirecteur || isScolarite || isResponsableScolarite || isConsultant,
    canModifierEnseignants: isAdmin || isDirecteur || isScolarite || isResponsableScolarite,
    canGererPaiements: peutGererFinances,
    
    // Données personnelles
    canViewOwnData: isEtudiant || isEnseignant,
    canViewOwnNotes: isEtudiant,
    canViewOwnPaiements: isEtudiant,
    canViewOwnPresences: isEtudiant,
    canViewOwnEmploiTemps: isEtudiant,
  };

  return {
    user,
    isAdmin,
    isDirecteur,
    isScolarite,
    isResponsableScolarite,
    isEnseignant,
    isEtudiant,
    isConsultant,
    isChefFiliere,
    isDirectionFinanciere,
    isDirectionGenerale,
    isCoordinateurPedagogique,
    isAuthenticated,
    permissions
  };
};

